"use client";

import Link from "next/link";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Menu, X } from "lucide-react";

export default function TermsPage() {
  const [isLightTheme, setIsLightTheme] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "light") {
      setIsLightTheme(true);
      document.documentElement.classList.add("light");
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = !isLightTheme;
    setIsLightTheme(newTheme);
    localStorage.setItem("theme", newTheme ? "light" : "dark");
    if (newTheme) {
      document.documentElement.classList.add("light");
    } else {
      document.documentElement.classList.remove("light");
    }
  };

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/contact", label: "Contact" },
    { href: "/terms", label: "Terms" },
    { href: "/privacy", label: "Privacy" },
    { href: "/cookies", label: "Cookies" },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col landing-theme">
      {/* ============================================ */}
      {/* NAVIGATION - MATCHING LANDING PAGE */}
      {/* ============================================ */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/30 glass-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo - proportional sizing */}
            <Link href="/" className="flex items-center">
              <span className="text-xl portrait:text-[clamp(1.25rem,2.5vw,1.75rem)] font-bold text-foreground tracking-tight">Lateral</span>
            </Link>

            {/* Separator - Desktop only (lg+) */}
            <div className="hidden lg:block separator-line mx-6" />

            {/* Menu Items - Desktop only (lg+), proportional sizing */}
            <div className="hidden lg:flex items-center gap-6 portrait:gap-[clamp(1.5rem,2vw,2rem)] flex-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-sm portrait:text-[clamp(0.875rem,1.2vw,1rem)] text-foreground-secondary hover:text-foreground transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Auth Buttons + Theme Toggle - Desktop (lg+), proportional sizing */}
            <div className="hidden lg:flex items-center gap-3 portrait:gap-[clamp(0.75rem,1.2vw,1rem)]">
              {/* Theme Toggle - Desktop */}
              <button
                onClick={toggleTheme}
                className="ml-2 p-2 portrait:p-[clamp(0.5rem,0.8vw,0.75rem)] rounded-lg text-foreground-secondary hover:text-foreground hover:bg-white/5 transition-all"
                aria-label="Toggle theme"
              >
                {isLightTheme ? (
                  <Moon className="w-5 h-5 portrait:w-[clamp(1.25rem,2vw,1.75rem)] portrait:h-[clamp(1.25rem,2vw,1.75rem)]" />
                ) : (
                  <Sun className="w-5 h-5 portrait:w-[clamp(1.25rem,2vw,1.75rem)] portrait:h-[clamp(1.25rem,2vw,1.75rem)]" />
                )}
              </button>
            </div>

            {/* Mobile/Tablet: Theme Toggle + Hamburger (below lg) */}
            <div className="lg:hidden flex items-center gap-2">
              {/* Theme Toggle - Mobile */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-foreground-secondary hover:text-foreground transition-colors"
                aria-label="Toggle theme"
              >
                {isLightTheme ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-foreground hover:text-foreground-secondary transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden absolute top-full left-0 right-0 bg-background/95 backdrop-blur-md border-b border-border/30 shadow-lg"
            >
              <div className="py-6 flex flex-col items-center gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-base text-foreground-secondary hover:text-foreground transition-colors py-2"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ============================================ */}
      {/* MAIN CONTENT - NARROWER WIDTH */}
      {/* ============================================ */}
      <main className="flex-1 pt-[73px]">
        <div className="max-w-4xl mx-auto px-6 py-12 md:py-16 lg:py-20">
          {/* Page Title */}
          <div className="mb-12">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Terms and conditions
            </h1>
            <p className="text-lg text-foreground-secondary mb-8">
              Last Updated: January 4, 2026
            </p>
            <div className="h-px bg-foreground/10" />
          </div>

          <div className="prose prose-invert max-w-none">
            <p className="text-foreground-secondary text-lg mb-8 leading-relaxed">
              Welcome to <span className="text-foreground font-medium">Lateral Teams Apps</span>. These Terms and Conditions (&quot;Terms&quot;) constitute a
              legally binding agreement between you (&quot;User,&quot; &quot;Customer,&quot; or &quot;You&quot;) and{" "}
              <span className="text-foreground font-medium">Lateral LTD</span> (&quot;Company,&quot; &quot;We,&quot; or &quot;Us&quot;), a company registered in England and Wales.
            </p>

            <div className="bg-foreground/5 border border-foreground/10 rounded-xl p-6 mb-12 hover:border-foreground/20 transition-colors">
              <h3 className="text-foreground font-semibold mb-3">Company Details:</h3>
              <ul className="space-y-2 text-foreground-secondary">
                <li><strong className="text-foreground">Registered Address:</strong> 20-22 Wenlock Road, London, UK</li>
                <li><strong className="text-foreground">Contact Email:</strong> info@lateralteams.com</li>
              </ul>
            </div>

            <p className="text-foreground-secondary mb-12 leading-relaxed">
              By registering for, accessing, or using our SaaS platform for AI-assisted marketing content generation (the &quot;Service&quot;), you agree to be bound by these Terms.
            </p>

            {/* Section 1 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">1. The Service</h2>
              <p className="text-foreground-secondary mb-4 leading-relaxed">
                Lateral Teams Apps provides a platform that utilizes Artificial Intelligence (AI) to assist users in generating, editing, and publishing marketing content.
              </p>
              <ul className="space-y-3 text-foreground-secondary list-disc pl-5">
                <li>
                  <strong className="text-foreground">AI Disclaimer:</strong> You acknowledge that the Service uses probabilistic AI algorithms. We do not guarantee that the content generated will be perfectly accurate, unique, or suitable for your specific needs. The AI may occasionally generate incorrect information (&quot;hallucinations&quot;).
                </li>
                <li>
                  <strong className="text-foreground">Human Oversight:</strong> You agree to review and verify all AI-generated content before publishing it. You are solely responsible for the final published output.
                </li>
              </ul>
            </section>

            {/* Section 2 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">2. Account Registration</h2>
              <ul className="space-y-3 text-foreground-secondary list-disc pl-5">
                <li>
                  <strong className="text-foreground">Eligibility:</strong> You must be at least 18 years old to use the Service.
                </li>
                <li>
                  <strong className="text-foreground">Security:</strong> You are responsible for maintaining the confidentiality of your login credentials. You are liable for all activities that occur under your account.
                </li>
                <li>
                  <strong className="text-foreground">Notification:</strong> You must notify us immediately at info@lateralteams.com of any unauthorized use of your account.
                </li>
              </ul>
            </section>

            {/* Section 3 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">3. Intellectual Property Rights</h2>
              <ul className="space-y-3 text-foreground-secondary list-disc pl-5">
                <li>
                  <strong className="text-foreground">Our IP:</strong> Lateral LTD retains all rights, title, and interest in the Service, including the software, source code, logos, and algorithms.
                </li>
                <li>
                  <strong className="text-foreground">Your Input:</strong> You retain ownership of all text, data, and prompts you input into the Service (&quot;Input&quot;).
                </li>
                <li>
                  <strong className="text-foreground">AI Output:</strong> To the extent permitted by applicable law, Lateral LTD assigns to you all rights, title, and interest in the content generated by the Service based on your Input (&quot;Output&quot;).
                </li>
                <li>
                  <strong className="text-foreground">Usage License:</strong> You grant Lateral LTD a worldwide, non-exclusive, royalty-free license to use your Input and Output solely for the purpose of providing and improving the Service (e.g., processing your request).
                </li>
              </ul>
            </section>

            {/* Section 4 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">4. Acceptable Use Policy</h2>
              <p className="text-foreground-secondary mb-4 leading-relaxed">You agree <strong className="text-foreground">not</strong> to use the Service to:</p>
              <ol className="list-decimal list-inside space-y-2 text-foreground-secondary pl-4">
                <li>Generate content that is unlawful, defamatory, pornographic, fraudulent, or promotes hate speech.</li>
                <li>Infringe upon the intellectual property or privacy rights of third parties.</li>
                <li>Generate content for malicious purposes, such as &quot;deepfakes&quot; or misinformation campaigns.</li>
                <li>Reverse engineer, decompile, or attempt to extract the source code or underlying models of the Service.</li>
              </ol>
              <p className="text-foreground mt-4 font-semibold">
                We reserve the right to suspend or terminate your account immediately without refund if you violate these rules.
              </p>
            </section>

            {/* Section 5 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">5. Subscription and Billing</h2>
              <ul className="space-y-3 text-foreground-secondary list-disc pl-5">
                <li>
                  <strong className="text-foreground">Fees:</strong> The Service is billed on a subscription basis (monthly or annually). Payments are non-refundable unless required by UK law.
                </li>
                <li>
                  <strong className="text-foreground">Taxes:</strong> Prices may be exclusive of VAT or other applicable taxes, which you are responsible for paying.
                </li>
                <li>
                  <strong className="text-foreground">Cancellation:</strong> You may cancel your subscription at any time via your account settings. Access will continue until the end of the current billing cycle.
                </li>
              </ul>
            </section>

            {/* Section 6 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">6. Limitation of Liability</h2>
              <p className="text-foreground-secondary mb-4 leading-relaxed">To the maximum extent permitted by English law:</p>
              <ul className="space-y-3 text-foreground-secondary list-disc pl-5">
                <li>The Service is provided &quot;AS IS&quot; and &quot;AS AVAILABLE.&quot;</li>
                <li>Lateral LTD shall not be liable for any indirect, incidental, special, or consequential damages, including loss of profits, data, or business reputation.</li>
                <li>Our total liability for any claim arising out of these Terms shall not exceed the amount you paid to us in the <strong className="text-foreground">12 months preceding</strong> the claim.</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">7. Indemnification</h2>
              <p className="text-foreground-secondary leading-relaxed">
                You agree to indemnify and hold Lateral LTD harmless from any claims, damages, or expenses arising out of your use of the Service, your violation of these Terms, or your violation of any third-party rights (including copyright infringement regarding the content you publish).
              </p>
            </section>

            {/* Section 8 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">8. Governing Law and Jurisdiction</h2>
              <p className="text-foreground-secondary leading-relaxed">
                These Terms are governed by the laws of <strong className="text-foreground">England and Wales</strong>. Any disputes shall be subject to the exclusive jurisdiction of the courts of London, United Kingdom.
              </p>
            </section>

            {/* Footer */}
            <div className="mt-16 pt-8 border-t border-foreground/10">
              <p className="text-foreground-secondary text-sm">
                If you have any questions about these Terms, please contact us at{" "}
                <a href="mailto:info@lateralteams.com" className="text-accent hover:underline">
                  info@lateralteams.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* ============================================ */}
      {/* FOOTER - MATCHING LANDING PAGE */}
      {/* ============================================ */}
      <footer className="relative border-t border-border pt-6 pb-0 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          {/* Back to Top */}
          <div className="flex justify-end mb-6">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="text-sm text-foreground-secondary hover:text-foreground transition-colors flex items-center gap-1"
            >
              Back to top <span className="text-base">↑</span>
            </a>
          </div>

          {/* Footer content - responsive structure */}
          {/* Mobile & Tablet (< lg): Centered layout */}
          <div className="lg:hidden flex flex-col items-center gap-6 pb-6">
            <Link href="/" className="flex items-center">
              <span className="text-xl font-bold text-foreground tracking-tight">Lateral</span>
            </Link>
            
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-foreground-secondary">
              <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
              <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link href="/cookies" className="hover:text-foreground transition-colors">Cookies</Link>
              <Link href="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            </div>
            <div className="text-center space-y-1">
              <p className="text-sm text-foreground-secondary">
                20-22 Wenlock Road, London, UK
              </p>
              <p className="text-sm text-foreground-secondary">
                info@lateralteams.com
              </p>
            </div>
            <p className="text-sm text-foreground-muted text-center">
              © 2026 Lateral Teams · All rights reserved
            </p>
          </div>

          {/* Desktop (lg+): Multi-row layout */}
          <div className="hidden lg:block pb-6">
            {/* Top row: Logo + Main Nav */}
            <div className="flex items-center gap-8 mb-6">
              <Link href="/" className="flex items-center shrink-0">
                <span className="text-xl font-bold text-foreground tracking-tight">Lateral</span>
              </Link>
            </div>
            {/* Bottom row: Legal + Company Info + Copyright */}
            <div className="flex items-center justify-between gap-8">
              <div className="flex items-center gap-5 text-sm text-foreground-secondary">
                <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
                <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
                <Link href="/cookies" className="hover:text-foreground transition-colors">Cookies</Link>
                <Link href="/contact" className="hover:text-foreground transition-colors">Contact</Link>
              </div>
              <div className="flex items-center gap-6 text-sm text-foreground-secondary">
                <span>20-22 Wenlock Road, London, UK</span>
                <span>·</span>
                <span>info@lateralteams.com</span>
              </div>
              <p className="text-sm text-foreground-muted shrink-0">
                © 2026 Lateral Teams · All rights reserved
              </p>
            </div>
          </div>
        </div>

        {/* Large Lateral text - 60% visible, 40% hidden - consistent on all screens */}
        <div className="relative h-[14vw] mt-4 overflow-hidden">
          <span
            className="absolute left-1/2 -translate-x-1/2 bottom-[-55%] text-[23vw] font-bold leading-none tracking-[0.02em] select-none pointer-events-none whitespace-nowrap"
            style={{ color: "var(--watermark-color)" }}
          >
            Lateral
          </span>
        </div>
      </footer>
    </div>
  );
}